/**
 * Created by Ning Jiang on 3/28/2019.
 */

import {MultiSprites} from "@nolimitcity/slot-game/bin/core/displayobject/MultiSprites";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {
    ISymbolSizeConfig,
    ISymbolStateConfig,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TweenLite} from "gsap";
import {PixiesVsPiratesReelSymbol} from "./PixiesVsPiratesReelSymbol";

export class PPMidSymbol extends PixiesVsPiratesReelSymbol {

    private static _bgTint:{ [symName:string]:number } = {
        M1 : 0xFFD24D,
        M2 : 0xFF323A,
        M3 : 0x80FF62,
        M4 : 0x8348F2,
        M5 : 0x327BFF,
        M6 : 0x48CAF2
    };
    private static _bgNoWinTint:{ [symName:string]:number } = {
        M1 : 0x4d3e1f,
        M2 : 0x4d0f11,
        M3 : 0x264d1d,
        M4 : 0x231340,
        M5 : 0x0f254d,
        M6 : 0x133540
    };
    private static _frameTint:{ [symName:string]:number } = {
        M1 : 0xFFCC38,
        M2 : 0xFFCC38,
        M3 : 0x57B75F,
        M4 : 0xA764D4,
        M5 : 0x638DD5,
        M6 : 0x4FACBE
    };
    private static _frameNoWinTint:{ [symName:string]:number } = {
        M1 : 0x4d3e11,
        M2 : 0x4d3e11,
        M3 : 0x0f1f10,
        M4 : 0x2e1c3b,
        M5 : 0x1c283d,
        M6 : 0x152e33
    };

    // the offsetY is the offset from the full height image top to the targeting center point.
    protected static _symbolAnchorOffsetY:{ [symName:string]:number } = {
        M1 : 36,
        M2 : 48,
        M3 : 46,
        M4 : 42,
        M5 : 34,
        M6 : 36
    };

    protected static _sideMaskOffset:number = 8;

    private _background:MultiSprites | null;
    private _frameBgContainer:PIXI.Container | null;
    private _frameContainer:PIXI.Container | null;
    private _frameTop:PIXI.Sprite;
    private _frameBottom:PIXI.Sprite;
    private _frameSides:MultiSprites;
    private _innerMask:PIXI.Graphics | null;
    private _winGlowMask:PIXI.Graphics | null;
    private _anchorY:number;

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig, size?:ISymbolSizeConfig) {
        super(symName, reelId, state, size);
    }

    protected initAnimations():void {
        super.initAnimations();
        this._background = new MultiSprites({
                normal : {
                    textures : GameResources.getTextures("midSymBg"),
                    anchor : [0.5, 0.5]
                },
                spin : {
                    textures : GameResources.getTextures("midSymBg_spin"),
                    anchor : [0.5, 0.5]
                }
            }
        );
        for(let name in this._background.sprites) {
            this._background.sprites[name].tint = PPMidSymbol._bgTint[this.symName];
            if(name === "normal") {
                //console.log(this._background.sprites[name]);
                (<PIXI.Sprite[]>this._background.sprites[name].children).forEach((sprite:PIXI.Sprite) => {
                    sprite.name = "frameAndBg";
                });
            }
        }

        this._background.name = "frameAndBg";
        this.addChild(this._background);

        if(this._winGlow) {
            this.addChild(this._winGlow);
            this._winGlow.name = "frameAndBg";
        }

        this._frameBgContainer = new PIXI.Container();
        this._frameBgContainer.name = "frameAndBg";
        this.addChild(this._frameBgContainer);

        this._frameContainer = new PIXI.Container();
        // this.addChild(this._frameContainer);

        this._frameSides = new MultiSprites({
                normal : {
                    textures : GameResources.getTextures("midSymFrameSides"),
                    anchor : [0.5, 0.5]
                },
                spin : {
                    textures : GameResources.getTextures("midSymFrameSides_spin"),
                    anchor : [0.5, 0.5]
                }
            }
        );
        for(let name in this._frameSides.sprites) {
            this._frameSides.sprites[name].tint = PPMidSymbol._frameTint[this.symName];
            this._frameSides.sprites[name].name = "frameAndBg";
        }
        this._frameContainer.addChild(this._frameSides);
        //this._frameContainer.name = "frameContainer";

        this._frameTop = new PIXI.Sprite(GameResources.getTextures("midSymFrameTop")[0]);
        this._frameTop.anchor.set(0.5, 0);
        this._frameTop.tint = PPMidSymbol._frameTint[this.symName];
        this._frameContainer.addChild(this._frameTop);

        this._frameBottom = new PIXI.Sprite(GameResources.getTextures("midSymFrameBottom")[0]);
        this._frameBottom.anchor.set(0.5, 1);
        this._frameBottom.tint = PPMidSymbol._frameTint[this.symName];
        this._frameContainer.addChild(this._frameBottom);
    }

    protected initState(stateConfig:ISymbolStateConfig):boolean {
        if(super.initState(stateConfig)) {
            if(this.symHeight < GameConfig.instance.SYMBOL_HEIGHT) {
                this.addChild(this._frameContainer!);

                // Will prevent visibility toggle
                this._frameContainer!.name = "mask";
            }
            if(this._innerMask) {
                this._innerMask.name = "mask";
                this._stateAnimations[stateConfig.keyword]!.mask = this._innerMask;
                this._stateAnimations[stateConfig.keyword]!.name = "mask";
            }
            this._stateAnimations[stateConfig.keyword]!.anchor.y = this._anchorY;
            if(this._bounceSprite) {
                this._bounceSprite.anchor.y = this._anchorY;
            }
            return true;
        }
        return false;
    }

    protected addNewStateAnimation(timeline:gsap.TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);
        if(this.currentState!.keyword === GameConfig.instance.SYMBOL_STATES.idle!.keyword) {
            stateAnimation.stopLoop();
        }
        if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.spin.keyword) {
            timeline.add(this._background!.playSprite("spin",
                {
                    duration : newState.fadeInDuration,
                    vars : [{toVars : {alpha : 1}}]
                },
                {
                    duration : newState.fadeOutDuration,
                    vars : [{toVars : {alpha : 0}}]
                }), delay);
            timeline.add(this._frameSides!.playSprite("spin",
                {
                    duration : newState.fadeInDuration,
                    vars : [{toVars : {alpha : 1}}]
                },
                {
                    duration : newState.fadeOutDuration,
                    vars : [{toVars : {alpha : 0}}]
                }), delay);
        } else {
            timeline.add(this._background!.playSprite("normal",
                {
                    duration : newState.fadeInDuration,
                    vars : [{toVars : {alpha : 1}}]
                },
                {
                    duration : newState.fadeOutDuration,
                    vars : [{toVars : {alpha : 0}}]
                }), delay);
            timeline.add(this._frameSides!.playSprite("normal",
                {
                    duration : newState.fadeInDuration,
                    vars : [{toVars : {alpha : 1}}]
                },
                {
                    duration : newState.fadeOutDuration,
                    vars : [{toVars : {alpha : 0}}]
                }), delay);
        }

        if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.noWin!.keyword) {
            if(this._background) {
                for(let spriteName in this._background.sprites) {
                    const bgSprite:TimelineSprite = this._background.sprites[spriteName];
                    timeline.add(TweenLite.to(bgSprite, newState.fadeInDuration, {pixi : {tint : PPMidSymbol._bgNoWinTint[this.symName]}}), delay); // TODO:
                }
            }
            if(this._frameSides) {
                for(let spriteName in this._frameSides.sprites) {
                    const sidesSprite:TimelineSprite = this._frameSides.sprites[spriteName];
                    timeline.add(TweenLite.to(sidesSprite, newState.fadeInDuration, {pixi : {tint : PPMidSymbol._frameNoWinTint[this.symName]}}), delay);
                }
            }
            if(this._frameTop) {
                timeline.add(TweenLite.to(this._frameTop, newState.fadeInDuration, {pixi : {tint : PPMidSymbol._frameNoWinTint[this.symName]}}), delay);
            }
            if(this._frameBottom) {
                timeline.add(TweenLite.to(this._frameBottom, newState.fadeInDuration, {pixi : {tint : PPMidSymbol._frameNoWinTint[this.symName]}}), delay);
            }
        } else {
            if(this._background) {
                for(let spriteName in this._background.sprites) {
                    const bgSprite:TimelineSprite = this._background.sprites[spriteName];
                    timeline.add(TweenLite.to(bgSprite, newState.fadeInDuration, {pixi : {tint : PPMidSymbol._bgTint[this.symName]}}), delay);
                }
            }
            if(this._frameSides) {
                for(let spriteName in this._frameSides.sprites) {
                    const sidesSprite:TimelineSprite = this._frameSides.sprites[spriteName];
                    timeline.add(TweenLite.to(sidesSprite, newState.fadeInDuration, {pixi : {tint : PPMidSymbol._frameTint[this.symName]}}), delay);
                }
            }
            if(this._frameTop) {
                timeline.add(TweenLite.to(this._frameTop, newState.fadeInDuration, {pixi : {tint : PPMidSymbol._frameTint[this.symName]}}), delay);
            }
            if(this._frameBottom) {
                timeline.add(TweenLite.to(this._frameBottom, newState.fadeInDuration, {pixi : {tint : PPMidSymbol._frameTint[this.symName]}}), delay);
            }
        }


        if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.idle!.keyword) {
            stateAnimation.playLoop();
        }
    }

    protected reset(remove:boolean):void {
        if(this._background) {
            this.removeChild(this._background);
            this._background = null;
        }

        if(this._frameContainer) {
            this._frameContainer.removeChildren();
            if(this._frameContainer.parent) {
                this._frameContainer.parent.removeChild(this._frameContainer);
            }
            this._frameContainer = null;
        }

        if(this._frameBgContainer) {
            this.removeChild(this._frameBgContainer);
            this._frameBgContainer = null;
        }

        super.reset(remove);
    }

    protected resetSymMask():void {
        super.resetSymMask();

        if(this._innerMask) {
            for(let stateName in this._stateAnimations) {
                if(this._stateAnimations[stateName]) {
                }
            }
            this.removeChild(this._innerMask);
            this._innerMask = null;
        }

        if(this._winGlowMask) {
            this.removeChild(this._winGlowMask);
            this._winGlowMask = null;
        }
    }

    protected updateHeightDisplay():void {
        super.updateHeightDisplay();

        this._frameTop.y = -Math.ceil(this.symHeight / 2);
        this._frameBottom.y = Math.ceil(this.symHeight / 2);

        if(this.symHeight === GameConfig.instance.SYMBOL_HEIGHT) {
            this._frameBgContainer!.addChild(this._frameContainer!);
        } else {
            this.addChild(this._frameContainer!);
        }

        this._anchorY = MathHelper.roundToDecimals(Math.min(Math.max(PPMidSymbol._symbolAnchorOffsetY[this.symName], this.symHeight / 2), GameConfig.instance.SYMBOL_HEIGHT - this.symHeight / 2) / GameConfig.instance.SYMBOL_HEIGHT, 2);
        for(let stateName in this._stateAnimations) {
            const state = this._stateAnimations[stateName];
            if(state != null) {
                state.anchor.y = this._anchorY;
            }
        }

        if(this._winGlow) {
            this._winGlow.scale.set(this._displayScale, this._displayScale);
        }
    }

    protected playBounceAnimation(onEnd:(trackEntry:PIXI.spine.core.TrackEntry) => void = () => {}, track:number = 0, animationName:string = "animation", loop:boolean = false, timeScale:number = 1):void {
        if(this._bounceSpine && this._bounceSprite) {

            this._bounceSprite.visible = true;
            this._bounceSpine.visible = true;

            if(this._bounceSpine.skeleton && this._bounceSpine.state) {
                this._bounceSpine.skeleton.setToSetupPose();
                this._bounceSpine.state.tracks = [];
            }

            if(this._bounceSprite && this.symHeight !== GameConfig.instance.SYMBOL_HEIGHT) {
                if(this.symName === "M2") { // Red Pirate
                    this._bounceSprite.y = (this._bounceSprite.y + 7.7715) / this._anchorY; // TODO: Magic!
                } else if(this.symName === "M5") { // Blue fairy
                    this._bounceSprite.y = (this._bounceSprite.y + 7.785) / this._anchorY; // TODO: Magic!
                } else {
                    this._bounceSprite.y = (this._bounceSprite.y + 7.7765) / this._anchorY; // TODO: Magic!
                }

            }

            let noBounceChildren:PIXI.Sprite [] = [];
            (<PIXI.Sprite[]>this.children).forEach((sprite:PIXI.Sprite, index:number) => {
                if(sprite!.name === "bounce") {
                    const frameIndex:number = (this._frameContainer && this._frameContainer.parent) ? this._frameContainer.parent.getChildIndex(this._frameContainer) : 0;
                    if(frameIndex !== null && frameIndex - 1 > -1 && this.symHeight !== GameConfig.instance.SYMBOL_HEIGHT) {
                        if(this._bounceSprite) {
                            this.setChildIndex(this._bounceSprite, frameIndex - 1);
                            this._bounceSprite.mask = this._innerMask!;
                        }
                    } else {
                        if(this._bounceSprite) {
                            this.setChildIndex(this._bounceSprite, this.children.length - 1);
                        }
                    }
                } else {
                    noBounceChildren.push(sprite);
                }
            });

            if(this._bounceSpine.state) {
                this._bounceSpine.state.timeScale = timeScale;
                this._bounceSpine.state.setAnimation(track, animationName, loop);

                let end = (entry:PIXI.spine.core.TrackEntry) => {
                    onEnd(entry);
                    if(this._bounceSpine && this._bounceSpine.state && this._bounceSprite) {
                        this._bounceSpine.state.removeListener(listener);
                        this._bounceSprite.visible = false;

                        this._bounceSpine = null;
                        this._bounceSprite = null;
                    }

                    noBounceChildren.forEach((sprite) => {
                        sprite.visible = true;
                    });
                };
                let listener = {complete : end};

                this._bounceSpine.state.addListener({
                    //event: function(entry, event) { console.log('event fired '+event.data+' at track' + entry.trackIndex) },
                    complete : (entry) => end(entry)
                    //start: function(entry) { console.log('animation is set at '+entry.trackIndex) },
                    //end: function(entry) { console.log('animation was ended at '+entry.trackIndex) }
                });
            }


        }

    }

    protected drawSymMask():boolean {
        if(super.drawSymMask()) {
            if(this.symHeight < GameConfig.instance.SYMBOL_HEIGHT) {
                this._innerMask = new PIXI.Graphics();
                this._innerMask.beginFill(0x00FF00, 1);
                this._innerMask.drawRect(-GameConfig.instance.SYMBOL_WIDTH / 2 + PPMidSymbol._sideMaskOffset, -Math.floor(this.symHeight / 2), GameConfig.instance.SYMBOL_WIDTH - PPMidSymbol._sideMaskOffset * 2, this.symHeight);

                this.addChild(this._innerMask);
                if(this._winGlow) {
                    this._winGlow.mask = this._innerMask;
                }
                for(let stateName in this._stateAnimations) {
                    const state = this._stateAnimations[stateName];
                    if(state != null) {
                        state.mask = this._innerMask;
                    }
                }
            }
            return true;
        } else {
            if(this._winGlow) {
                this._winGlowMask = new PIXI.Graphics();
                this._winGlowMask.beginFill(0xFF0000, 1);
                this._winGlowMask.drawRect(-GameConfig.instance.SYMBOL_WIDTH / 2 + PPMidSymbol._sideMaskOffset, -Math.floor(this.symHeight / 2), GameConfig.instance.SYMBOL_WIDTH - PPMidSymbol._sideMaskOffset * 2, this.symHeight);
                this.addChild(this._winGlowMask);
                this._winGlow.mask = this._winGlowMask;
            }
            return false;
        }
    }
}